import React from 'react';
import styles from './StenosisTable.module.scss';
import OptionSelector from '@components/OptionSelector/OptionSelector';
import { scheduleRender } from '@features/analysis/rendering/Panels/renderState';

export interface SegmentRow {
  vessel: string;
  proximal: string;
  mid: string;
  distal: string;
}

export type SegmentTargets = Record<
  string,
  {
    proximal?: number;
    mid?: number;
    distal?: number;
  }
>;
export type SegmentSelection = {
  branch: string;
  segment: 'proximal' | 'mid' | 'distal';
  targetIndex: number;
};

export interface TableReportProps {
  width?: number;
  height?: number;
  data?: string[];
  segmentRows?: SegmentRow[];
  segmentTargets?: SegmentTargets;
  branchRows?: string[][];
  onSegmentSelect?: (selection: SegmentSelection | null) => void;
  onSegmentSave?: (selection: SegmentSelection) => void;
}

type Location = '' | 'proximal' | 'mid' | 'distal';

type LineModel = {
  lineIndex: number;
  raw: string;
  branch: string;
  location: Location;
  tissueVal?: number;
  percentVal?: PercentBucketId;
  tissueChanged?: boolean;
  percentChanged?: boolean;
};

type State = {
  editMode: boolean;
  models?: LineModel[];
  previewLines?: string[];
  lastSavedSig?: string;
  selectedCellId?: string | null;
  viewMode: 'table' | 'legacy';
  selectedSegment?: SegmentSelection | null;
};

type PercentBucketDef = {
  id: number;
  label: string;
  classKey: string;
  mid: number;
};
const PERCENT_BUCKETS = [
  { id: 0, label: '0%', classKey: 'p0', mid: 0 },
  { id: 1, label: '1-24%', classKey: 'p1_24', mid: 12 },
  { id: 2, label: '25-49%', classKey: 'p25_49', mid: 37 },
  { id: 3, label: '50-69%', classKey: 'p50_69', mid: 60 },
  { id: 4, label: '≥70%', classKey: 'pge70', mid: 85 },
] as PercentBucketDef[];
type PercentBucket = (typeof PERCENT_BUCKETS)[number];
type PercentBucketId = PercentBucket['id'];
type PercentClassKey = PercentBucket['classKey'];

const PERCENT_OPTIONS = PERCENT_BUCKETS.map((b) => ({
  label: b.label,
  value: b.id,
}));
const MID_BY_ID = new Map<PercentBucketId, number>(
  PERCENT_BUCKETS.map((b) => [b.id, b.mid]),
);

export default class StenosisTable extends React.PureComponent<
  TableReportProps,
  State
> {
  declare calciumIdx: number;
  constructor(props: TableReportProps) {
    super(props);
    this.state = {
      editMode: false,
      selectedCellId: null,
      viewMode: 'table',
      selectedSegment: null,
    };
  }

  private tissueOptions = [
    { label: 'noncalcified', value: 0 },
    { label: 'calcified', value: 1 },
    { label: 'mixed', value: 2 },
  ] as const;

  private isSkippableForPayload(line: string): boolean {
    const s = String(line ?? '').trim();
    if (!s) return true;
    if (/^\s*calcium\s*scor(?:e|ing)\b/i.test(s)) return true;
    if (/\bunremarkable\b/i.test(s)) return true;
    return false;
  }

  private currentLines(): string[] {
    return (this.state.previewLines ?? this.props.data ?? []).map((s) =>
      String(s ?? ''),
    );
  }
  private getCalciumScore(): string {
    const all = this.currentLines();
    let calciumHeader: string | null = null;
    let calciumIdx = -1;
    const reCal = /^\s*Calcium\s*Scor(?:e|ing)\s*[:=]\s*(.+?)\s*$/i;
    for (let i = 0; i < all.length; i++) {
      const s = String(all[i] ?? '').trim();
      const m = reCal.exec(s);
      if (m) {
        const rawVal = m[1].trim();
        const num = Number(rawVal.replace(/,/g, ''));
        calciumHeader = `Calcium score: ${Number.isFinite(num) ? num : rawVal}`;
        calciumIdx = i;
        break;
      }
    }
    this.calciumIdx = calciumIdx;
    return calciumHeader ?? 'Calcium score: N/A';
  }
  private sig(lines: string[]) {
    return lines.join('\n');
  }

  private parseBranch(line: string): string {
    const m = /^\s*([A-Za-z0-9]{2,8})(?=\s*[:\-\u2013, ]|$)/i.exec(line);
    return m ? m[1].toUpperCase() : '';
  }

  private parseLocation(line: string): Location {
    const m = /\b(proximal|mid|distal)\b/i.exec(line);
    return m ? (m[1].toLowerCase() as Location) : '';
  }

  private classifyPercentText(txt: string): {
    id: PercentBucketId;
    classKey: PercentClassKey;
    displayLabel: string;
    midOverride?: number;
  } | null {
    const s = String(txt ?? '')
      .trim()
      .toLowerCase()
      .replace(/[\u2013]/g, '-')
      .replace(/~/g, '-');

    const single = /^(\d{1,3})\s*%$/.exec(s);
    if (single) {
      const n = Number(single[1]);
      if (n <= 0)
        return { id: 0, classKey: 'p0', displayLabel: '0%', midOverride: 0 };
      if (n >= 100)
        return {
          id: 4,
          classKey: 'pge70',
          displayLabel: '≥70%',
          midOverride: 100,
        };
      if (n >= 70) return { id: 4, classKey: 'pge70', displayLabel: '≥70%' };
      if (n >= 50) return { id: 3, classKey: 'p50_69', displayLabel: '50-69%' };
      if (n >= 25) return { id: 2, classKey: 'p25_49', displayLabel: '25-49%' };
      if (n >= 1) return { id: 1, classKey: 'p1_24', displayLabel: '1-24%' };
      return { id: 1, classKey: 'p1_24', displayLabel: '1-24%' };
    }

    const ge = /^(?:>=|≥)\s*(\d{1,3})\s*%$/.exec(s);
    const geFallback = /^\?\?\s*(\d{1,3})\s*%$/.exec(s);
    const geMatch = ge ?? geFallback;
    if (geMatch) {
      const n = Number(geMatch[1]);
      if (n >= 70) return { id: 4, classKey: 'pge70', displayLabel: '≥70%' };
      if (n >= 50) return { id: 3, classKey: 'p50_69', displayLabel: '50-69%' };
      if (n >= 25) return { id: 2, classKey: 'p25_49', displayLabel: '25-49%' };
      if (n >= 1) return { id: 1, classKey: 'p1_24', displayLabel: '1-24%' };
      return { id: 1, classKey: 'p1_24', displayLabel: '1-24%' };
    }

    const range = /^(\d{1,3})\s*-\s*(\d{1,3})\s*%$/.exec(s);
    if (range) {
      const a = Number(range[1]);
      const b = Number(range[2]);
      const mid = Math.round((a + b) / 2);
      if (mid <= 0)
        return { id: 0, classKey: 'p0', displayLabel: '0%', midOverride: 0 };
      if (mid >= 70) return { id: 4, classKey: 'pge70', displayLabel: '≥70%' };
      if (mid >= 50)
        return { id: 3, classKey: 'p50_69', displayLabel: '50-69%' };
      if (mid >= 25)
        return { id: 2, classKey: 'p25_49', displayLabel: '25-49%' };
      if (mid >= 1) return { id: 1, classKey: 'p1_24', displayLabel: '1-24%' };
      return { id: 1, classKey: 'p1_24', displayLabel: '1-24%' };
    }

    return null;
  }

  private displayPercentLabel(txt: string) {
    const c = this.classifyPercentText(txt);
    return c ? c.displayLabel : txt;
  }
  private percentClassFromText(txt: string) {
    const c = this.classifyPercentText(txt);
    return c?.classKey ?? '';
  }
  private computeMidFromText(txt: string): number | null {
    const c = this.classifyPercentText(txt);
    if (!c) return null;
    if (typeof c.midOverride === 'number') return c.midOverride;
    return MID_BY_ID.get(c.id) ?? null;
  }

  private findTissueToken(line: string) {
    const re = /\b(?:non(?:-|\s)?calcified|calcified|mixed)\b/gi;
    const m = re.exec(line);
    if (!m) return;
    const text = m[0];
    const norm = text
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '');
    const val =
      norm === 'noncalcified'
        ? 0
        : norm === 'calcified'
          ? 1
          : norm === 'mixed'
            ? 2
            : undefined;
    return { start: m.index, end: m.index + text.length, text, val };
  }
  private findPercentToken(line: string) {
    const re =
      /(?:>=|≥|\?\?\s*)?\d{1,3}\s*(?:[\u2013\-~]\s*\d{1,3})?(?:\.\d+)?\s*%/gi;
    const m = re.exec(line);
    if (!m) return;
    const text = m[0];
    const c = this.classifyPercentText(text);
    return c
      ? {
          start: m.index,
          end: m.index + text.length,
          text,
          val: c.id as PercentBucketId,
        }
      : undefined;
  }

  componentDidUpdate(prev: TableReportProps) {
    if (prev.data !== this.props.data && this.state.previewLines) {
      const arrived = this.sig(
        (this.props.data ?? []).map((s) => String(s ?? '')),
      );
      if (this.state.lastSavedSig && arrived === this.state.lastSavedSig) {
        this.setState({ previewLines: undefined, lastSavedSig: undefined });
      } else {
        this.setState({ previewLines: undefined });
      }
    }
  }

  private handleEdit = () => {
    const models: LineModel[] = this.currentLines().map((line, i) => {
      const branch = this.parseBranch(line);
      const location = this.parseLocation(line);
      const tissue = this.findTissueToken(line);
      const percent = this.findPercentToken(line);

      return {
        lineIndex: i,
        raw: line,
        branch,
        location,
        tissueVal: tissue?.val,
        percentVal: percent?.val,
        tissueChanged: false,
        percentChanged: false,
      };
    });

    this.setState({ editMode: true, models });
  };

  private handleSave = async () => {
    if (!this.state.models) return;
    const rebuilt: string[] = [];

    for (const m of this.state.models) {
      const line = m.raw;

      const tTok = this.findTissueToken(line);
      const pTok = this.findPercentToken(line);

      if (!tTok && !pTok) {
        rebuilt.push(line);
        continue;
      }

      const spans: Array<{
        kind: 'tissue' | 'percent';
        start: number;
        end: number;
      }> = [];
      if (tTok)
        spans.push({ kind: 'tissue', start: tTok.start, end: tTok.end });
      if (pTok)
        spans.push({ kind: 'percent', start: pTok.start, end: pTok.end });
      spans.sort((a, b) => a.start - b.start);

      const parts: string[] = [];
      let cursor = 0;
      for (const sp of spans) {
        if (cursor < sp.start) parts.push(line.slice(cursor, sp.start));

        if (sp.kind === 'tissue') {
          const label = m.tissueChanged
            ? (this.tissueOptions.find((o) => o.value === m.tissueVal)?.label ??
              tTok?.text ??
              '')
            : (tTok?.text ?? '');
          parts.push(label);
        } else {
          const label = m.percentChanged
            ? (PERCENT_BUCKETS.find(
                (o) => o.id === (m.percentVal as PercentBucketId),
              )?.label ??
              pTok?.text ??
              '')
            : (pTok?.text ?? '');
          parts.push(label);
        }
        cursor = sp.end;
      }
      parts.push(line.slice(cursor));
      rebuilt.push(parts.join(''));
    }

    const toCopy = rebuilt.join('\n');

    const lesions: Array<{
      branch: string;
      location: Location;
      percentMid: number | null;
      tissue: 'noncalcified' | 'calcified' | 'mixed' | null;
      lineIndex: number;
    }> = [];

    for (const m of this.state.models) {
      if (this.isSkippableForPayload(m.raw)) continue;

      let percentMid: number | null = null;
      const pTok = this.findPercentToken(m.raw);
      if (pTok) {
        percentMid = m.percentChanged
          ? (MID_BY_ID.get(
              (m.percentVal as PercentBucketId) ?? (-1 as PercentBucketId),
            ) ?? null)
          : this.computeMidFromText(pTok.text);
      }

      const tissue =
        typeof m.tissueVal === 'number'
          ? (this.tissueOptions.find((o) => o.value === m.tissueVal)?.label as
              | 'noncalcified'
              | 'calcified'
              | 'mixed')
          : null;

      lesions.push({
        branch: m.branch ?? '',
        location: m.location ?? '',
        percentMid,
        tissue,
        lineIndex: m.lineIndex,
      });
    }

    scheduleRender('Report', {
      type: 'stenosisUpdate',
      result: toCopy,
      persent: lesions,
    });

    this.setState({
      previewLines: rebuilt,
      lastSavedSig: toCopy,
      editMode: false,
      models: undefined,
    });
  };

  private handleReset = async () => {
    scheduleRender('Report', { type: 'reset' });
  };

  private handleCopy = async () => {
    const toCopy = this.currentLines().join('\n');
    try {
      await navigator.clipboard.writeText(toCopy);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = toCopy;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  private handleSelectStenosis(line: string[], index: number) {
    if (line.length > 0) {
      let samebranch = '';
      let positionidx = 0;
      for (let i = 0; i < line.length; i++) {
        const nameTemp = this.parseBranch(line[i]);

        if (samebranch !== nameTemp) {
          samebranch = nameTemp;
          positionidx = 0;
        } else {
          positionidx++;
        }
        if (index === i) break;
      }

      if (samebranch !== '' && positionidx >= 0) {
        scheduleRender('Report', {
          type: 'selectStenosis',
          branchName: samebranch,
          positionIndex: positionidx,
        });
      }
    }
  }

  private handleCellClick = (
    e: React.MouseEvent,
    cellId: string,
    isSelectable: boolean,
  ) => {
    e.stopPropagation();
    if (!isSelectable) {
      this.setState({ selectedCellId: null });
      return;
    }
    this.setState({ selectedCellId: cellId, selectedSegment: null });
    this.props.onSegmentSelect?.(null);
  };

  private handleTableCellClick = (
    e: React.MouseEvent,
    cellId: string,
    isSelectable: boolean,
    branch: string,
    position: 'proximal' | 'mid' | 'distal',
  ) => {
    e.stopPropagation();
    if (!isSelectable) {
      this.setState({ selectedCellId: null, selectedSegment: null });
      return;
    }

    const targetIndex = this.props.segmentTargets?.[branch]?.[position];
    const selection =
      typeof targetIndex === 'number' && targetIndex >= 0
        ? { branch, segment: position, targetIndex }
        : null;

    this.setState({ selectedCellId: cellId, selectedSegment: selection });
    this.props.onSegmentSelect?.(selection);
    if (selection) {
      scheduleRender('Report', {
        type: 'selectStenosis',
        branchName: branch,
        positionIndex: targetIndex,
      });
    }
  };

  private clearSelection = () => {
    this.setState({ selectedCellId: null, selectedSegment: null });
    this.props.onSegmentSelect?.(null);
  };

  private toggleViewMode = () => {
    this.setState((prev) => ({
      viewMode: prev.viewMode === 'table' ? 'legacy' : 'table',
      editMode: false,
      models: undefined,
      previewLines: undefined,
      lastSavedSig: undefined,
      selectedCellId: null,
      selectedSegment: null,
    }));
  };

  private handleLegacyLineClick = (
    e: React.MouseEvent,
    lines: string[],
    index: number,
  ) => {
    e.stopPropagation();
    this.setState({ selectedCellId: `legacy-${index}` });
    this.handleSelectStenosis(lines, index);
  };

  private updateModelValue = (
    lineIndex: number,
    field: 'tissue' | 'percent',
    value: number,
  ) => {
    this.setState((prev) => {
      if (!prev.models) return null;
      const next = prev.models.map((m) => {
        if (m.lineIndex !== lineIndex) return m;
        if (field === 'tissue') {
          return { ...m, tissueVal: value, tissueChanged: true };
        }
        return { ...m, percentVal: value, percentChanged: true };
      });
      return { models: next };
    });
  };

  render() {
    const segmentRows = this.props.segmentRows ?? [
      { vessel: 'LM', proximal: '', mid: '0', distal: '' },
      { vessel: 'LAD', proximal: '0', mid: '0', distal: '0' },
      { vessel: 'LCX', proximal: '0', mid: '-', distal: '0' },
      { vessel: 'RCA', proximal: '0', mid: '0', distal: '0' },
    ];

    const branchColumns = ['D1', 'D2', 'RI', 'OM1', 'OM2', 'PDA', 'PLB'];
    const branchRows = this.props.branchRows ?? [
      ['0', '0', '0', '0', '0', '0', '0'],
    ];
    const isLegacy = this.state.viewMode === 'legacy';
    const lines = this.currentLines();
    const sideRow = branchRows[0] ?? [];
    const lmRow = segmentRows.find((row) => row.vessel === 'LM');
    const lmValue =
      lmRow?.proximal && lmRow.proximal !== '-'
        ? lmRow.proximal
        : lmRow?.mid && lmRow.mid !== '-'
          ? lmRow.mid
          : lmRow?.distal ?? '';

    return (
      <div
        className={styles.stenosisTable}
        style={
          isLegacy
            ? { width: this.props.width, height: this.props.height }
            : undefined
        }
        aria-label="report"
        onClick={this.clearSelection}
      >
        {isLegacy ? (
          <div className={styles.legacyContainer}>
            <div className={styles.legacyHeader}>
              <div className={styles.legacyTitle}>Interpretation</div>
              <div className={styles.legacyActions}>
                {this.state.editMode ? (
                  <>
                    <button
                      type="button"
                      className={styles.legacyActionBtn}
                      onClick={this.handleSave}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.legacyActionBtn}
                      onClick={() => this.setState({ editMode: false })}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.legacyActionBtn}
                      onClick={this.handleReset}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className={styles.legacyActionBtn}
                      onClick={this.handleCopy}
                    >
                      Copy
                    </button>
                    <button
                      type="button"
                      className={styles.legacyActionBtn}
                      onClick={this.handleEdit}
                    >
                      Edit
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className={styles.legacyBody}>
              {this.state.editMode && this.state.models
                ? this.state.models.map((m) => {
                    const line = m.raw;
                    const tTok = this.findTissueToken(line);
                    const pTok = this.findPercentToken(line);

                    if (!tTok && !pTok) {
                      return (
                        <div className={styles.legacyLine} key={m.lineIndex}>
                          <span className={styles.legacyText}>{line}</span>
                        </div>
                      );
                    }

                    const spans: Array<{
                      kind: 'tissue' | 'percent';
                      start: number;
                      end: number;
                    }> = [];
                    if (tTok)
                      spans.push({
                        kind: 'tissue',
                        start: tTok.start,
                        end: tTok.end,
                      });
                    if (pTok)
                      spans.push({
                        kind: 'percent',
                        start: pTok.start,
                        end: pTok.end,
                      });
                    spans.sort((a, b) => a.start - b.start);

                    const pieces: React.ReactNode[] = [];
                    let cursor = 0;

                    spans.forEach((sp, idx) => {
                      if (cursor < sp.start) {
                        pieces.push(
                          <span
                            key={`txt-${idx}-${cursor}`}
                            className={styles.legacyText}
                          >
                            {line.slice(cursor, sp.start)}
                          </span>,
                        );
                      }

                      if (sp.kind === 'tissue') {
                        const value = (
                          m.tissueChanged
                            ? m.tissueVal
                            : (m.tissueVal ?? tTok?.val ?? 0)
                        ) as number;
                        pieces.push(
                          <span
                            key={`tissue-${idx}`}
                            className={styles.legacyInlineOptionWrap}
                          >
                            <OptionSelector
                              options={this.tissueOptions as any}
                              value={value}
                              onChange={(val) =>
                                this.updateModelValue(
                                  m.lineIndex,
                                  'tissue',
                                  val,
                                )
                              }
                              disabled={false}
                              className={styles.legacyInlineOption}
                              isRow={false}
                            />
                          </span>,
                        );
                      } else {
                        const value = (
                          m.percentChanged
                            ? m.percentVal
                            : (m.percentVal ??
                              pTok?.val ??
                              PERCENT_BUCKETS[0].id)
                        ) as PercentBucketId;
                        pieces.push(
                          <span
                            key={`percent-${idx}`}
                            className={styles.legacyInlineOptionWrap}
                          >
                            <OptionSelector
                              options={PERCENT_OPTIONS as any}
                              value={value}
                              onChange={(val) =>
                                this.updateModelValue(
                                  m.lineIndex,
                                  'percent',
                                  val,
                                )
                              }
                              disabled={false}
                              className={styles.legacyInlineOption}
                              isRow={false}
                            />
                          </span>,
                        );
                      }
                      cursor = sp.end;
                    });

                    pieces.push(
                      <span
                        key={`tail-${m.lineIndex}`}
                        className={styles.legacyText}
                      >
                        {line.slice(cursor)}
                      </span>,
                    );

                    return (
                      <div className={styles.legacyLine} key={m.lineIndex}>
                        {pieces}
                      </div>
                    );
                  })
                : (() => {
                    let calciumHeader: string | null = null;
                    let calciumIdx = -1;
                    const reCal =
                      /^\s*Calcium\s*Scor(?:e|ing)\s*[:=]\s*(.+?)\s*$/i;
                    for (let i = 0; i < lines.length; i++) {
                      const s = String(lines[i] ?? '').trim();
                      const m = reCal.exec(s);
                      if (m) {
                        const rawVal = m[1].trim();
                        const num = Number(rawVal.replace(/,/g, ''));
                        calciumHeader = `Calcium score: ${Number.isFinite(num) ? num : rawVal}`;
                        calciumIdx = i;
                        break;
                      }
                    }
                    const bodyLines =
                      calciumIdx >= 0
                        ? [
                            ...lines.slice(0, calciumIdx),
                            ...lines.slice(calciumIdx + 1),
                          ]
                        : lines;

                    return (
                      <>
                        {calciumHeader && (
                          <div
                            className={`${styles.legacyLine} ${styles.legacyCalcium}`}
                          >
                            {calciumHeader}
                          </div>
                        )}

                        {bodyLines.map((line, idx) => {
                          const tokens: Array<{
                            start: number;
                            end: number;
                            text: string;
                          }> = [];
                          const re =
                            /(?:>=|≥|\?\?\s*)?\d{1,3}\s*(?:[\u2013\-~]\s*\d{1,3})?(?:\.\d+)?\s*%/gi;
                          let m: RegExpExecArray | null;
                          while ((m = re.exec(line))) {
                            tokens.push({
                              start: m.index,
                              end: m.index + m[0].length,
                              text: m[0],
                            });
                            if (re.lastIndex === m.index) re.lastIndex++;
                          }
                          tokens.sort((a, b) => a.start - b.start);

                          if (tokens.length === 0) {
                            return (
                              <div className={styles.legacyLine} key={idx}>
                                <span className={styles.legacyText}>
                                  {line}
                                </span>
                              </div>
                            );
                          }

                          const nodes: React.ReactNode[] = [];
                          let cursor = 0;
                          tokens.forEach((t, i) => {
                            if (cursor < t.start)
                              nodes.push(
                                <span
                                  key={`t-${i}-${cursor}`}
                                  className={styles.legacyText}
                                >
                                  {line.slice(cursor, t.start)}
                                </span>,
                              );
                            const cls = this.percentClassFromText(t.text);
                            const shown = this.displayPercentLabel(t.text);
                            nodes.push(
                              <span
                                key={`p-${i}`}
                                className={`${styles.legacyPercent} ${styles[cls]}`}
                              >
                                {shown}
                              </span>,
                            );
                            cursor = t.end;
                          });
                          nodes.push(
                            <span
                              key={`tail-${idx}`}
                              className={styles.legacyText}
                            >
                              {line.slice(cursor)}
                            </span>,
                          );

                          return (
                            <div
                              className={styles.legacySelect}
                              key={idx}
                              onClick={(e) =>
                                this.handleLegacyLineClick(e, bodyLines, idx)
                              }
                            >
                              {nodes}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
            </div>

            {!this.state.editMode && (
              <div className={styles.legacyLegend}>
                {PERCENT_BUCKETS.map((b) => (
                  <span className={styles.legacyLegendItem} key={b.id}>
                    <i
                      className={`${styles.legacyDot} ${styles[b.classKey]}`}
                    />{' '}
                    {b.label}
                  </span>
                ))}
              </div>
            )}
            <div className={styles.modeToggleRow}>
              <button
                type="button"
                className={styles.modeToggleButton}
                onClick={this.toggleViewMode}
              >
                Stenosis Table
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.stenosisContent}>
            <div className={styles.stenosisHeaderTitle}>
              <div className={styles.stenosisHeader}>
                <div className={styles.headerTitle}>
                  <span>Stenosis(%)</span>
                </div>
              </div>
              <div className={styles.stenosisTitle2}>
                <div className={styles.titleContainer}>
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.headerLine}></div>
                      <span>Main Branches</span>
                    </div>
                    <div className={styles.sectionTable}>
                      <div className={styles.table}>
                        <div className={styles.tableRow}>
                          <div
                            className={`${styles.headerCells} ${styles.cellLabel}`}
                          >
                            <span></span>
                          </div>
                          <div className={styles.headerCells}>Proximal</div>
                          <div className={styles.headerCells}>Mid</div>
                          <div className={styles.headerCells}>Distal</div>
                        </div>
                        <div className={styles.tableRow}>
                          <div
                            className={`${styles.dataHeaderCells} ${styles.cellLabel}`}
                          >
                            <span>LM</span>
                          </div>
                          <div
                            className={`${styles.dataCells} ${styles.cellCenter} ${
                              lmValue !== '-' &&
                              lmValue !== '' &&
                              Number(lmValue) > 0
                                ? ''
                                : styles.cellCursorDefault
                            }`}
                          >
                            {lmValue}
                          </div>
                        </div>
                        {segmentRows
                          .filter((row) => row.vessel !== 'LM')
                          .map((row) => (
                            <div className={styles.tableRow} key={row.vessel}>
                              <div
                                className={`${styles.dataHeaderCells} ${styles.cellLabel}`}
                              >
                                <span>{row.vessel}</span>
                              </div>
                              {(['proximal', 'mid', 'distal'] as const).map(
                                (key) => {
                                  const value = row[key];
                                  const numericValue = Number(value);
                                  const isZeroValue =
                                    value !== '' &&
                                    value !== '-' &&
                                    Number.isFinite(numericValue) &&
                                    numericValue <= 0;
                                  const cellId = `segment-${row.vessel}-${key}`;
                                  const isSelectable =
                                    value !== '-' &&
                                    value !== '' &&
                                    !isZeroValue;
                                  const isSelected =
                                    this.state.selectedCellId === cellId;

                                  const className = isSelected
                                    ? `${styles.dataCells} ${styles.cellSelected}`
                                    : !isSelectable
                                      ? `${styles.dataCells} ${styles.cellCursorDefault}`
                                      : styles.dataCells;
                                  return (
                                    <div
                                      key={key}
                                      className={className}
                                      onClick={(e) =>
                                        this.handleTableCellClick(
                                          e,
                                          cellId,
                                          isSelectable,
                                          row.vessel,
                                          key,
                                        )
                                      }
                                    >
                                      {value}
                                    </div>
                                  );
                                },
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.titleContainer}>
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <div className={styles.headerLine}></div>
                      <span>Side Branches</span>
                    </div>
                    <div className={styles.sectionTable}>
                      {branchColumns.map((col, colIndex) => {
                        const value = sideRow[colIndex] ?? '';
                        const cellId = `branch-0-${colIndex}`;
                        const numericValue = Number(value);
                        const isZeroValue =
                          value !== '' &&
                          value !== '-' &&
                          Number.isFinite(numericValue) &&
                          numericValue <= 0;

                        const isSelectable = value !== '' && value !== '-' && !isZeroValue;
                        const isSelected = this.state.selectedCellId === cellId;
                        const className = isSelected
                          ? `${styles.columnData} ${styles.cellSelected}`
                          : !isSelectable
                            ? `${styles.columnData} ${styles.cursorDefault}`
                            : styles.columnData;
                        return (
                          <div className={styles.tableColumns} key={col}>
                            <div className={styles.columnHeader}>{col}</div>
                            <div
                              className={className}
                              onClick={(e) =>
                                this.handleCellClick(e, cellId, isSelectable)
                              }
                            >
                              {value}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (this.state.selectedSegment) {
                      this.props.onSegmentSave?.(this.state.selectedSegment);
                    }
                  }}
                >
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
